import { IAnimTarget, IAnimTdwParams } from './anim-interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { getQuery } from 'sutils';
import { ACTIVITY } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-constants';
import { MEDIA, GROUP } from './anim-constants';
import { isAudioElement, isVideoElement } from '@src/slide/domain/ui-model/elements/element-judge';
import { nodeTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import { SequenceTypes, convertHSLToRGB, convertRGBToHSL, ICTTLByHslColorTransform } from '@tencent/slide-sdk-model';
import { IAnimList } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    TimingPropertyConverter,
} from '@src/slide/domain/ui-model/converter/node-property/page/timing-property-converter';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IShapeBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { HslColor } from '@tencent/slide-sdk-model/src/props/common/color-props/color';
import { rgbStrToObject } from '@src/slide/infrastructure/module/independent/color/transform';

const PPT_PLAYER = 'pptplayer';


export const isUndefined = (value: any): boolean => typeof value === 'undefined';
export const isNumber = (value: any): boolean => typeof value === 'number';
export const isFunction = (value: any): boolean => typeof value === 'function';

/**
 * 获取fromStyle与toStyle之间有哪些属性不同，动画变换时只更改这些不同的属性
 * 以解决同一对象多个动画同时播放的问题(多个动画同时改了相同属性的冲突情况不去处理，本地office也有bug)
*/
export function getDifferentProperty(fromStyle, toStyle): string[] {
    if (Array.isArray(toStyle)) {
        const differentProperty: string[] = [];
        toStyle.forEach((item) => {
            Object.keys(fromStyle).forEach((key) => {
                if (fromStyle[key] !== item[key] && !isUndefined(item[key])) {
                    differentProperty.push(key);
                }
            });
        });
        return Array.from(new Set(differentProperty));
    }
    const differentProperty: string[] = [];
    Object.keys(fromStyle).forEach((key) => {
        if (fromStyle[key] !== toStyle[key] && !isUndefined(toStyle[key])) {
            differentProperty.push(key);
        }
    });
    return differentProperty;
}

/**
 * 判断主队列第一个动画是否为立即播放动画
 * @param mainSeqSdkData 主队列动画sdk数据
 * @returns boolean
 *
 * mainSeqSdkData?.cTn?.childTnLst 通过clickEffect划分
 *  → [[[],[]], [[],[]],...otherClickGroupInfo] 三维数组
 *
 * firstMainSeqEffectInfo?.cTn?.childTnLst 同一组动画通过afterEffect划分
 * →[[],[]]二维数组
 *
 * firstClickOrAfterAnimInfo?.cTn?.childTnLst = [eachAnimInfo, ...]
 * → [clickEffectInfo, withEffectInfo, withEffectInfo, ...]
 * or → [withEffectInfo, withEffectInfo, ...]
 *  or → [afterEffectInfo, withEffectInfo, withEffectInfo, ...]
 */
export const checkFirstAnimIsWithEffect = (mainSeqSdkData: {[key: string]: any}): boolean => {
    let firstAnimIsWithEffect = false;
    const firstMainSeqEffectInfo = mainSeqSdkData?.cTn?.childTnLst?.[0];
    const firstClickOrAfterAnimInfo = firstMainSeqEffectInfo?.cTn?.childTnLst?.[0];
    const firstAnimInfo = firstClickOrAfterAnimInfo?.cTn?.childTnLst?.[0];
    if ([nodeTypes.WITH_EFFECT, nodeTypes.AFTEREFFECT].includes(firstAnimInfo?.cTn?.nodeType)) {
        firstAnimIsWithEffect = true;
    }
    return firstAnimIsWithEffect;
};
// 数组扁平化处理
export function flatten(arr): any[] {
    if (!arr) {
        return [];
    }
    return arr.reduce((prev, next) => prev.concat(Array.isArray(next) ? flatten(next) : next), []);
}


export const handleAnimReportParams = (timingSdkData): IAnimTdwParams => {
    const numInfo = {
        entr: 0,
        emph: 0,
        exit: 0,
        path: 0,
        mainSeq: 0,
        interactiveSeq: 0,
    };
    return numInfo;
};


export const checkHasAnimation = (modelId: string, slideModel?: ISlideModel): boolean => TimingPropertyConverter
    .checkHasAnimation(modelId, slideModel?.timingModel, slideModel?.timing);

/**
 * 获取元素与所绑定的动画顺序的映射
 * @param slideModel
 * @returns Map {key1 => [order1, order2, order3], key2 => [order3, order5]}
 * 动画order数组去重，且由小到大有序
 */
export const getAnimTargetKeyOrderMap = (slideModel?: ISlideModel): Map<string, number[]> => {
    if (!slideModel) return new Map();
    const keyOrderMap = new Map();
    slideModel.children?.forEach((elementModel: IElementBaseModel) => {
        const { animList, key } = elementModel;
        if (!animList?.length) return;
        animList.forEach((animModel) => {
            const order = animModel?.order;
            if (isUndefined(order)) return;
            const orderList = keyOrderMap.get(key);
            if (!orderList) {
                keyOrderMap.set(key, [order]);
                return;
            }
            if (!orderList.includes(order)) orderList.push(order);
        });
    });

    return keyOrderMap;
};

export const isSpecialActivity = getQuery(ACTIVITY.name) === ACTIVITY.value;

// 是否为独立播放器页面
export const isPPTPlayer = (window as any).entry === PPT_PLAYER;

export const getMediaType = (elementModel: IElementBaseModel): string => {
    if (isVideoElement(elementModel)) return MEDIA.VIDEO;
    if (isAudioElement(elementModel)) return MEDIA.AUDIO;
    return '';
};

// eslint-disable-next-line arrow-body-style
export const isMediaElement = (elementModel: IElementBaseModel): boolean => {
    return isVideoElement(elementModel) || isAudioElement(elementModel);
};

/**
 *  获取当前页的主动画
 * @param slideModel 当前页model
 * @returns AnimationModel[]
 */
export const  getMainAnimList = (slideModel: ISlideModel): IAnimList | undefined => {
    if (!slideModel) return;
    const mainSeqType = SequenceTypes.MAIN_SEQ;
    const mainSeqAnimObj = slideModel.animList?.find(animListItem => animListItem.sequenceTypes === mainSeqType);
    if (!mainSeqAnimObj) return;
    return mainSeqAnimObj.data;
};

export const setPointerEvents = (animTarget, value) => {
    const eleType = animTarget.factoryOptions.model.type;
    if (eleType !== GROUP) {
        animTarget.wrapperInstance.pointerEvents = value;
        return;
    }
    const [groupComponent] = animTarget.wrapperInstance.children;

    groupComponent.children.forEach((child) => {
        const childWrapper = child.wrapperInstance;
        childWrapper.pointerEvents = value;
    });
};

// 检查当前页是否有音频
export const checkSlideHasAudio = (model) => {
    if (model?.type === UIModelType.IMAGE && model?.audio) {
        return true;
    }
    return model?.children?.some(checkSlideHasAudio);
};

const isTrigger = (model, control) => {
    // 是否有触发器
    const { iaFragmentLengthInfos = {}, currentPage = 0 } = control;
    const iaIdMap = iaFragmentLengthInfos[currentPage] || {};
    const iaIdList = Object.keys(iaIdMap);

    if (model.type === UIModelType.SLIDE && iaIdList.length > 0) return true;

    return iaIdList.includes(model.id);
};

const isMedia = model => (model?.type === UIModelType.IMAGE && (model.audio || model.video));

const isLink = (model) => {
    if (model?.props?.link?.href) return true;

    if (model?.type !== UIModelType.SHAPE) return false;

    return model?.paragraphList?.some(({ textRuns }) => textRuns.some(textRun => !!textRun?.styles?.href));
};

// 判断当前页是否有可点击元素,触发器、音视频、链接均属于可点击元素
export const checkHasClickEle = (model, control) => {
    // 是否有触发器
    if (isTrigger(model, control)) return true;

    // 是否有音视频
    if (isMedia(model)) return true;

    // 是否有链接
    if (isLink(model)) return true;

    return model?.children?.some(checkHasClickEle) || false;
};

export function shouldDisableAnimCacheAndFilter(animTarget: IAnimTarget): boolean {
    // 3d 场景的 filter 渲染和动画缓存还暂时不支持，需要手动禁用一下
    return !!animTarget.factoryOptions.model?.shape3d || !!animTarget.factoryOptions.model?.scene3d;
}

/**
 * 检查动画目标元素是否展示
 * @param shapeId string
 * @param slideModel ISlideModel
 * @returns boolean
 */
export function isElementVisible(shapeId: string, slideModel: ISlideModel): boolean {
    if (!shapeId || !slideModel) return false;
    const targetModel = slideModel.children?.find(childModel => childModel.id === shapeId);
    // hidden 1 表示元素需隐藏； 0 表示元素不隐藏，需展示
    return targetModel?.hidden === 0;
}


/**
* 获取填充颜色
* @returns ITDColor | undefined
*/
export function getFillColor(elementModel: IElementBaseModel): ITDColor | undefined {
    return elementModel.getBackgroundFill()?.getFillColor();
}

/**
* 获取字体颜色
* @returns ITDColor | undefined
*/
export function getFontColor(elementModel: IElementBaseModel): ITDColor | undefined {
    // office逻辑，选取第一个paragraph的字体颜色为动画的初始颜色
    const fontColor = (elementModel as IShapeBaseModel)?.paragraphList?.[0].textRuns?.[0]?.styles?.tdColor;
    return fontColor;
}

/**
* 获取边框颜色
* @returns ITDColor | undefined
*/
export function getLineColor(elementModel: IElementBaseModel): ITDColor | undefined {
    const lineColor = elementModel?.props.style.border?.tdColor as ITDColor;
    return lineColor;
}

/**
 * 获取字体大小
 * @param elementModel
 * @returns number | undefined
 */
export function getFontSize(elementModel: IElementBaseModel): number | undefined {
    // office逻辑，选取第一个paragraph的字体大小为动画的初始fontSize
    const fontSize =  (elementModel as IShapeBaseModel)?.paragraphList?.[0].textRuns?.[0]?.styles?.fontSize;
    return fontSize;
}

export function calculateHSLColor(hsl?: ICTTLByHslColorTransform, originalColor?: ITDColor): string | undefined {
    if (!originalColor) return;
    if (typeof hsl?.h === 'undefined'
            || typeof hsl?.s === 'undefined'
            || typeof hsl?.l === 'undefined'
    ) return ;

    let { h, s, l } = hsl;
    const { rgb } = originalColor;
    const originalHSL = rgbToHSL(rgb);
    if (!originalHSL) return;
    const { h: originalH, s: originalS, l: originalL } = originalHSL;

    // ooxml数据中h的精度值为6万分之一，s和l的精度值为10万分之一
    const newH = originalH + (h / 60000);
    h = newH > 0 ? newH : originalH + (360 + (h / 60000));
    s = Math.min(Math.max(originalS + (s / 100000), 0), 1); // 限定在[0,1]
    l = Math.min(Math.max(originalL + (l / 100000), 0), 1); // 限定在[0,1]

    // sdk中onvertHSLToRGB方法中，h取值范围为0-360，s和l取值范围为0-255，而不是常规理解的0-100%
    const color = hslToRgba({
        h,
        s: s * 255,
        l: l * 255,
    });
    return color;
}

export function rgbToHSL(rgb: string): HslColor | undefined {
    const rgbObj = rgbStrToObject(rgb);
    if (!rgbObj) return;

    const hsl = convertRGBToHSL(rgbObj);
    hsl.s = (hsl.s / 255); // 转换为 0-100%
    hsl.l = (hsl.l / 255); // 转换为 0-100%

    return hsl;
}

export function hslToRgba(hsl: HslColor): string | undefined {
    const rgbObj = convertHSLToRGB(hsl);
    const rgba = `rgba(${rgbObj.r}, ${rgbObj.g}, ${rgbObj.b}, 1)`;

    return rgba;
}
